import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import useMotionPreference from 'core/hooks/use-motion-preference';
import { getDisclosureTimeout, motionEaseOut } from 'core/components/theme/motion';
import useStyles from './disclosure.styles';

// Measure the current frame before canceling so a second click reverses from where it is now.
export default function Disclosure({ open, collapsedSize = 0, children, className }) {
  const classes = useStyles();
  const root = useRef();
  const body = useRef();
  const running = useRef();
  const initialized = useRef(false);
  const [mounted, setMounted] = useState(open);
  const { enabled } = useMotionPreference();
  if (open && !mounted) {
    setMounted(true);
  }

  useLayoutEffect(() => {
    const element = root.current;
    const from = element.getBoundingClientRect().height;
    running.current?.cancel();
    const styles = getComputedStyle(element);
    const verticalInsets = [
      styles.paddingTop,
      styles.paddingBottom,
      styles.borderTopWidth,
      styles.borderBottomWidth
    ].reduce((total, value) => total + (Number.parseFloat(value) || 0), 0);
    const target = open
      ? body.current.getBoundingClientRect().height + verticalInsets
      : Number.parseFloat(collapsedSize);
    const finish = () => {
      element.style.height = open ? 'auto' : `${target}px`;
      element.dataset.state = open ? 'open' : 'closed';
      if (!open) {
        setMounted(false);
      }
      running.current?.cancel();
      running.current = null;
    };
    const timeout = getDisclosureTimeout();
    const duration = enabled && initialized.current && timeout ? (open ? timeout.enter : timeout.exit) : 0;
    initialized.current = true;
    element.style.height = `${target}px`;
    element.dataset.state = open ? 'opening' : 'closing';
    if (!duration || !element.animate || from === target) {
      finish();
      return undefined;
    }
    const animation = element.animate([{ height: `${from}px` }, { height: `${target}px` }], {
      duration,
      easing: motionEaseOut
    });
    running.current = animation;
    animation.onfinish = finish;
    const settle = () => {
      if (running.current === animation) {
        finish();
      }
    };
    const settleKeyboardClick = (event) => {
      if (event.detail === 0) {
        settle();
      }
    };
    window.addEventListener('keydown', settle, true);
    window.addEventListener('click', settleKeyboardClick, true);
    return () => {
      window.removeEventListener('keydown', settle, true);
      window.removeEventListener('click', settleKeyboardClick, true);
    };
  }, [open, collapsedSize, enabled]);
  useEffect(() => () => running.current?.cancel(), []);

  return (
    <div
      ref={root}
      className={classNames(classes.root, className)}
      data-disclosure=""
      data-state={open ? 'open' : 'closed'}
    >
      <div ref={body} className={classes.body} inert={!open && Number.parseFloat(collapsedSize) === 0 ? '' : undefined}>
        {typeof children === 'function' ? children(mounted) : children}
      </div>
    </div>
  );
}

Disclosure.propTypes = {
  open: PropTypes.bool.isRequired,
  collapsedSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  className: PropTypes.string
};
